"use client";
import {
  asMenuItem,
  findOwnerMenuItem,
  getNestedMenu,
  useMenu,
} from "@/context/menu-context";
import { useUnit } from "@/context/unit-context";
import { Content } from "@/services/content/types";
import { usePathname } from "next/navigation";
import ActiveLink from "./active-link";
import HorizontalScroller from "./horizontal-scroller";

type SubNavigationProps = {
  menu?: Content.Menu;
};

const SubNavigation = ({ menu }: SubNavigationProps) => {
  const headerMenu = useMenu("menu-header");
  const pathname = usePathname();
  const { currentUnit } = useUnit();

  let resolved: Content.Menu | undefined = menu;
  let owner: Content.MenuItem | undefined;
  if (!resolved) {
    owner = findOwnerMenuItem(headerMenu, pathname);
    resolved = getNestedMenu(owner);
  }
  if (!resolved && currentUnit) {
    const unitSlug = currentUnit.replace(/^unit-/, "");
    const top = headerMenu?.fields?.menuItems ?? [];
    owner = top
      .map(asMenuItem)
      .find((mi) => mi?.fields?.item?.fields?.slug === unitSlug);
    resolved = getNestedMenu(owner);
  }

  const items = resolved?.fields?.menuItems
    ?.map(asMenuItem)
    .filter((x): x is Content.MenuItem => Boolean(x));

  if (!items || items.length === 0) return null;

  const ownerLabel = owner?.fields.label;

  return (
    <nav
      aria-label={ownerLabel ? `Undermeny för ${ownerLabel}` : "Undermeny"}
      className="bg-sand-500 relative"
    >
      <HorizontalScroller
        className="container px-0"
        activeSelector="[aria-current='page']"
        activeKey={pathname}
      >
        <ul className="flex whitespace-nowrap p-0">
          {items.map((menuItem) => {
            const rawHref =
              menuItem.fields.item?.fields?.slug ?? menuItem.fields.url ?? "#";
            const normalized =
              rawHref === "#" || rawHref.startsWith("/")
                ? rawHref
                : `/${rawHref}`;
            const href = normalized === "/index" ? "/" : normalized;
            return (
              <li key={menuItem.sys.id} className="shrink-0">
                <ActiveLink
                  className="inline-block text-gray-500 hover:underline font-normal text-sm md:text-base px-3 py-4 text-center"
                  activeClassName="!font-bold"
                  href={href}
                >
                  {menuItem.fields.label}
                </ActiveLink>
              </li>
            );
          })}
        </ul>
      </HorizontalScroller>
    </nav>
  );
};

export default SubNavigation;
